import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, useWindowDimensions } from 'react-native';
import { Image } from 'expo-image';
import { Crown, Star, Play, CreditCard, Smartphone } from 'lucide-react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/hooks/useTheme';
import { useCampaignConfig } from '@/hooks/useCampaignConfig';
import { useRandomSeriesCover } from '@/hooks/useRandomSeriesCover';
import { PriceOfferRow } from '@/components/PriceOfferRow';
import { Product } from '@/contexts/SubscriptionContext';
import { ensureWebpFormat } from '@/utils/imageResizer';

export type PaymentMethod = 'dcb' | 'revenuecat_web' | 'revenuecat_app';

interface PremiumSubscriptionCardProps {
  onSubscribe: (paymentMethod: PaymentMethod, productId?: string) => void;
  campaignCountriesLanguagesId?: string | null;
  products?: Product[];
  isPurchasing?: boolean;
  isIAPReady?: boolean;
}

export const PremiumSubscriptionCard: React.FC<PremiumSubscriptionCardProps> = ({
  onSubscribe,
  campaignCountriesLanguagesId,
  products = [],
  isPurchasing = false,
  isIAPReady = true,
}) => {
  const { t } = useTranslation();
  const { colors: { primary: primaryColor } } = useTheme();
  const { revenueCatEnabledApp, revenueCatEnabledWeb, dcbEnabledWeb } = useCampaignConfig();
  const coverUrl = useRandomSeriesCover(campaignCountriesLanguagesId ?? null);
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && width >= 768;

  const isWeeklyProduct = (productId: string) =>
    productId.toLowerCase().includes('week') || productId.toLowerCase().includes('weekly') || productId === '$rc_weekly';

  const sortedProducts = useMemo(() => {
    if (products.length <= 1) return products;
    return [...products].sort((a, b) => {
      const aWeekly = isWeeklyProduct(a.productId);
      const bWeekly = isWeeklyProduct(b.productId);
      if (aWeekly && !bWeekly) return -1;
      if (!aWeekly && bWeekly) return 1;
      return 0;
    });
  }, [products]);

  const offers = useMemo(() => {
    const list: Array<{
      id: string;
      method: PaymentMethod;
      productId?: string;
      badge?: string;
      icon: React.ReactNode;
      title: string;
      price: string;
      description: string;
    }> = [];

    if (Platform.OS === 'web') {
      if (dcbEnabledWeb) {
        list.push({
          id: 'dcb',
          method: 'dcb',
          icon: <Smartphone size={16} color="#ffffff" />,
          title: t('dcbSubscribeModalTitle'),
          price: '',
          description: t('dcbPaymentOfferDescription'),
        });
      }
      if (revenueCatEnabledWeb) {
        const rcProduct = sortedProducts[0];
        const price = rcProduct?.localizedPrice
          ? `${rcProduct.localizedPrice}${t('perMonth')}`
          : '';
        list.push({
          id: 'revenuecat_web',
          method: 'revenuecat_web',
          icon: <CreditCard size={16} color="#ffffff" />,
          title: t('cardAppleGooglePay'),
          price,
          description: t('cardPaymentDescription'),
        });
      }
    } else {
      if (revenueCatEnabledApp) {
        const isIOS = Platform.OS === 'ios';
        const storeTitle = isIOS ? t('appStorePaymentTitle') : t('playStorePaymentTitle');
        const storeDescription = isIOS ? t('appStorePaymentDescription') : t('playStorePaymentDescription');

        if (sortedProducts.length > 0) {
          sortedProducts.forEach((product) => {
            const isWeekly = isWeeklyProduct(product.productId);
            const periodSuffix = isWeekly ? t('perWeek') : t('perMonth');
            const price = product.localizedPrice
              ? `${product.localizedPrice}${periodSuffix}`
              : '';
            list.push({
              id: product.productId,
              method: 'revenuecat_app',
              productId: product.productId,
              icon: <CreditCard size={16} color="#ffffff" />,
              title: storeTitle,
              price,
              description: storeDescription,
            });
          });
        } else {
          list.push({
            id: 'revenuecat_app_default',
            method: 'revenuecat_app',
            icon: <CreditCard size={16} color="#ffffff" />,
            title: storeTitle,
            price: '',
            description: storeDescription,
          });
        }
      }
    }

    return list;
  }, [revenueCatEnabledWeb, revenueCatEnabledApp, dcbEnabledWeb, sortedProducts, t]);

  const nativeReady = Platform.OS === 'web' ? true : isIAPReady;

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const effectiveSelectedId = selectedId ?? (offers.length > 0 ? offers[0].id : null);

  const selectedOffer = offers.find((o) => o.id === effectiveSelectedId);

  const handleSubscribe = () => {
    if (!selectedOffer || isPurchasing) return;
    onSubscribe(selectedOffer.method, selectedOffer.productId);
  };

  return (
    <View style={styles.outerContainer}>
      <View style={styles.cardContainer}>
        {coverUrl ? (
          <View style={styles.bgImageContainer}>
            <Image
              source={{ uri: ensureWebpFormat(coverUrl) }}
              style={styles.bgImage}
              contentFit="cover"
              accessibilityLabel="Premium subscription background"
              {...(Platform.OS !== 'web' ? { blurRadius: 25 } : {})}
            />
            {Platform.OS === 'web' && (
              <View style={styles.webBlurOverlay} />
            )}
          </View>
        ) : (
          <View style={[styles.bgImageContainer, styles.bgFallback]} />
        )}

        <View style={styles.darkOverlay} />

        <View style={styles.content}>
          <View style={styles.headerRow}>
            <Crown size={22} color={primaryColor} />
            <Text style={styles.headerText}>
              {t('enterPremiumExperience')}{' '}
              <Text style={[styles.headerPremium, { color: primaryColor }]}>
                Premium
              </Text>
            </Text>
          </View>

          <View style={styles.offersSection}>
            {offers.map((offer) => (
              <PriceOfferRow
                key={offer.id}
                id={offer.id}
                selected={effectiveSelectedId === offer.id}
                onSelect={setSelectedId}
                badge={offer.badge}
                icon={offer.icon}
                title={offer.title}
                price={offer.price}
                description={offer.description}
                disabled={isPurchasing}
              />
            ))}
          </View>

          <View style={styles.vipSection}>
            <View style={styles.vipHeader}>
              <Star size={18} color="#F59E0B" fill="#F59E0B" />
              <Text style={styles.vipTitle}>{t('whyBecomeVip')}</Text>
            </View>
            <View style={styles.vipContent}>
              <View style={styles.playIconCircle}>
                <Play size={18} color={primaryColor} fill={primaryColor} />
              </View>
              <View style={styles.vipTextContainer}>
                <Text style={styles.vipCommunity}>{t('joinPremiumCommunity')}</Text>
                <Text style={styles.vipDescription}>{t('unlimitedAccessShort')}</Text>
              </View>
            </View>
          </View>

          <View style={isDesktopWeb ? styles.ctaWrapperDesktop : undefined}>
            <TouchableOpacity
              style={[
                styles.ctaButton,
                { backgroundColor: primaryColor },
                (isPurchasing || !nativeReady) && styles.ctaDisabled,
              ]}
              onPress={handleSubscribe}
              activeOpacity={0.8}
              disabled={isPurchasing || !selectedOffer || !nativeReady}
            >
              <Text style={styles.ctaText}>
                {isPurchasing
                  ? t('purchaseProcessing')
                  : !nativeReady
                    ? t('loadingOffers')
                    : t('upgradeToPremium')}
              </Text>
            </TouchableOpacity>

            <Text style={styles.reassurance}>{t('cancelAnytimeNoCommitment')}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    width: '100%',
  },
  cardContainer: {
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  bgImageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  bgImage: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web'
      ? ({ filter: 'blur(25px)', transform: [{ scale: 1.15 }] } as any)
      : {}),
  },
  webBlurOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  bgFallback: {
    backgroundColor: '#1a1a1a',
  },
  darkOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.72)',
  },
  content: {
    position: 'relative',
    padding: 20,
    zIndex: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 10,
  },
  headerText: {
    color: '#ffffff',
    fontSize: 17,
    fontWeight: '700',
    flex: 1,
  },
  headerPremium: {
    fontWeight: '800',
    fontStyle: 'italic',
  },
  offersSection: {
    marginBottom: 16,
  },
  ctaWrapperDesktop: {
    width: '50%',
    alignSelf: 'center',
  },
  ctaButton: {
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: 'center',
    marginBottom: 10,
  },
  ctaDisabled: {
    opacity: 0.6,
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 17,
    fontWeight: '700',
  },
  reassurance: {
    color: '#999',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
  },
  vipSection: {
    marginBottom: 20,
  },
  vipHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  vipTitle: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '700',
  },
  vipContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  playIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  vipTextContainer: {
    flex: 1,
  },
  vipCommunity: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  vipDescription: {
    color: '#bbb',
    fontSize: 12,
    lineHeight: 18,
  },
});
